import type { MapRef } from "@vis.gl/react-maplibre";
import { useMotionValueEvent } from "motion/react";
import { RefObject, useEffect, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { getAliveMap, isMapLibreReadyToPlacePins } from "@ryde/controls";
import { investigationFollowVehicleAtom } from "@ryde/shared";

import {
  FOLLOW_CHECK_INTERVAL_MS,
  FOLLOW_EASE_MS,
} from "../investigation.constants";
import { isInsideFollowBox, sampleAt } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";

/**
 * Keeps the playback vehicle on screen while the ride plays.
 *
 * The camera only re-centres once the vehicle leaves the middle of the viewport (see
 * `isInsideFollowBox`), so a ride that already fits is never nudged, and it only ever changes the
 * centre — the zoom the user chose, and the fit-to-ride that set it, both survive.
 *
 * Deliberately inert while paused: the playhead is also written on open and on every seek, and a
 * camera that jumped to the start position would undo the fit-to-ride the moment the panel opened.
 */
export const useInvestigationFollowVehicle = ({
  mapRef,
  isMapLoaded,
}: {
  mapRef: RefObject<MapRef | null>;
  isMapLoaded: boolean;
}): void => {
  const { model, isPlaying, playheadMs } = usePlaybackContext();
  const [isFollowing, setIsFollowing] = useRecoilState(
    investigationFollowVehicleAtom,
  );

  const isActive = isFollowing && isPlaying && isMapLoaded && Boolean(model);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const modelRef = useRef(model);
  modelRef.current = model;
  const lastCheckRef = useRef(0);

  /*
   * A drag means the user wants to look somewhere else, and a camera that pans straight back would
   * fight them. `dragstart` is user-only — our own `easeTo` fires `movestart`, never `dragstart` — so
   * this cannot switch itself off.
   */
  useEffect(() => {
    const map = getAliveMap(mapRef.current);

    if (!map || !isMapLoaded) {
      return undefined;
    }

    const stopFollowing = (): void => setIsFollowing(false);

    map.on("dragstart", stopFollowing);

    return () => {
      map.off("dragstart", stopFollowing);
    };
  }, [mapRef, isMapLoaded, setIsFollowing]);

  const keepVehicleInView = useEvent((ms: number): void => {
    const activeModel = modelRef.current;
    const map = getAliveMap(mapRef.current);

    if (!activeModel || !map || !isMapLibreReadyToPlacePins(map)) {
      return;
    }

    // Let the camera settle before measuring again: re-issuing `easeTo` mid-animation restarts it from
    // the new centre every frame, which reads as a stutter that never lands. This also holds off while
    // the user is pinch-zooming, which — unlike a drag — leaves following switched on.
    if (map.isMoving()) {
      return;
    }

    const { latLng } = sampleAt(activeModel, ms);

    if (!latLng) {
      return;
    }

    const [lat, lng] = latLng;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    const container = map.getContainer();
    const { x, y } = map.project([lng, lat]);

    if (
      isInsideFollowBox({
        x,
        y,
        width: container.clientWidth,
        height: container.clientHeight,
      })
    ) {
      return;
    }

    // No `essential`, on purpose: a viewer who asked for reduced motion gets the jump, not the glide.
    map.easeTo({ center: [lng, lat], duration: FOLLOW_EASE_MS });
  });

  // Catch up the moment following becomes active — pressing play after panning away re-frames at once.
  useEffect(() => {
    if (isActive) {
      keepVehicleInView(playheadMs.get());
    }
  }, [isActive, playheadMs, keepVehicleInView]);

  useMotionValueEvent(playheadMs, "change", (ms: number) => {
    if (!isActiveRef.current) {
      return;
    }

    const now = performance.now();

    if (now - lastCheckRef.current < FOLLOW_CHECK_INTERVAL_MS) {
      return;
    }

    lastCheckRef.current = now;
    keepVehicleInView(ms);
  });
};
